import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  Chip,
  Avatar,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { IntegrationCardSkeleton } from '../components/LoadingSkeleton';
import {
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { Integration, IntegrationCategory, IntegrationStatus } from '../types/integrations';
import { authorizeIntegration, getIntegrations, deleteIntegration } from '../services/api';

const allIntegrationsList: Omit<Integration, 'isConnected' | 'status' | 'connectedAt' | 'lastSync' | 'user_integration_id'>[] = [
  {
    id: 'box',
    name: 'Box',
    category: IntegrationCategory.DocumentRepository,
    description: 'Cloud content management and file sharing service',
    logoUrl: '/logos/box.png',
  },
  {
    id: 'dropbox',
    name: 'DropBox',
    category: IntegrationCategory.DocumentRepository,
    description: 'Cloud storage and file synchronization service',
    logoUrl: '/logos/dropbox.png',
  },
  {
    id: 'egnyte',
    name: 'Egnyte',
    category: IntegrationCategory.DocumentRepository,
    description: 'Enterprise file sharing and content governance platform',
    logoUrl: '/logos/egnyte.png',
  },
  {
    id: 'onedrive',
    name: 'OneDrive',
    category: IntegrationCategory.DocumentRepository,
    description: 'Microsoft\'s cloud storage service',
    logoUrl: '/logos/onedrive.png',
  },
  {
    id: 'googledrive',
    name: 'Google Drive',
    category: IntegrationCategory.DocumentRepository,
    description: 'Google\'s cloud storage and file sharing service',
    logoUrl: '/logos/googledrive.png',
  },
  {
    id: 'diligencevault',
    name: 'DiligenceVault',
    category: IntegrationCategory.DocumentRepository,
    description: 'Secure document sharing for due diligence processes',
    logoUrl: '/logos/diligencevault.png',
  },
  {
    id: 'intralink',
    name: 'Intralink',
    category: IntegrationCategory.DocumentRepository,
    description: 'Document management and workflow platform',
    logoUrl: '/logos/intralink.png',
  },
];

interface ApiIntegration {
  id: string;
  category: string;
  description: string;
  logo_url: string;
  tool_name: string;
  is_connected: boolean;
  created_at?: string;
  updated_at?: string;
  status: string;
}

const Integrations: React.FC = () => {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<Record<string, boolean>>({});
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const handleConnect = async (toolId: string) => {
    const width = 600;
    const height = 700;
    const left = (window.screen.width / 2) - (width / 2);
    const top = (window.screen.height / 2) - (height / 2);

    // Open the popup immediately on user click to avoid popup blockers
    const authPopup = window.open(
      '',
      'authPopup',
      `width=${width},height=${height},top=${top},left=${left}`
    );

    try {
      setIsLoading({ ...isLoading, [toolId]: true });
      
      const frontendUri = `${window.location.origin}/integrations`;
      const response = await authorizeIntegration(toolId, frontendUri);
      
      if (response && response.authorization_url && authPopup) {
        setSnackbar({
          open: true,
          message: 'Authorization URL retrieved. Opening popup...',
          severity: 'success',
        });
        authPopup.location.href = response.authorization_url;
        
        // Poll to check if popup is closed
        const pollTimer = setInterval(() => {
          if (authPopup.closed) {
            clearInterval(pollTimer);
            setIsLoading({ ...isLoading, [toolId]: false });
            
            // Refresh integrations after popup closes
            setTimeout(() => {
              fetchAndUpdateIntegrations();
              setSnackbar({
                open: true,
                message: 'Checking connection status...',
                severity: 'info',
              });
            }, 1000); // Wait 1 second for backend to process
          }
        }, 500);
      } else {
        if (authPopup) authPopup.close();
        setIsLoading({ ...isLoading, [toolId]: false });
        setSnackbar({
          open: true,
          message: 'Failed to get authorization URL',
          severity: 'error',
        });
      }
    } catch (error) {
      if (authPopup) authPopup.close();
      setIsLoading({ ...isLoading, [toolId]: false });
      console.error(`Error connecting to ${toolId}:`, error);
      setSnackbar({
        open: true,
        message: `Failed to connect to ${toolId}. Please try again.`,
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleDisconnect = async (integrationId: string) => {
    try {
      setIsLoading({ ...isLoading, [integrationId]: true });
      await deleteIntegration(integrationId);
      setSnackbar({
        open: true,
        message: 'Integration disconnected successfully.',
        severity: 'success',
      });
      // Refresh the integrations list
      fetchAndUpdateIntegrations();
    } catch (error) {
      console.error(`Error disconnecting integration ${integrationId}:`, error);
      setSnackbar({
        open: true,
        message: 'Failed to disconnect integration. Please try again.',
        severity: 'error',
      });
    } finally {
      setIsLoading({ ...isLoading, [integrationId]: false });
    }
  };

  const fetchAndUpdateIntegrations = async () => {
    setPageLoading(true);
    setIsSyncing(true);
    try {
      const connectedIntegrationsResponse = await getIntegrations();
      const connectedIntegrations: ApiIntegration[] = connectedIntegrationsResponse || [];

      const connectedMap = new Map(connectedIntegrations.map((i: ApiIntegration) => [i.tool_name, i]));

      const allIntegrations = allIntegrationsList.map(mockIntegration => {
        const isComingSoon = ['egnyte', 'diligencevault', 'intralink'].includes(mockIntegration.id);

        if (isComingSoon) {
          return {
            ...mockIntegration,
            isConnected: false,
            status: IntegrationStatus.ComingSoon,
          };
        }

        const connectedVersion = connectedMap.get(mockIntegration.id);
        if (connectedVersion) {
          return {
            ...mockIntegration,
            isConnected: true,
            status: IntegrationStatus.Connected,
            connectedAt: connectedVersion.created_at,
            lastSync: connectedVersion.updated_at,
            user_integration_id: connectedVersion.id,
          };
        } else {
          return {
            ...mockIntegration,
            isConnected: false,
            status: IntegrationStatus.Disconnected,
          };
        }
      });

      setIntegrations(allIntegrations);

    } catch (error) {
      console.error('Failed to fetch integrations status', error);
      setSnackbar({
        open: true,
        message: 'Could not fetch integration statuses. Displaying default data.',
        severity: 'warning',
      });
    } finally {
      setPageLoading(false);
      setIsSyncing(false);
    }
  };

  useEffect(() => {
    fetchAndUpdateIntegrations();
  }, []);

  if (pageLoading) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        {/* Skeleton Header */}
        <Paper sx={{ mb: 3, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4">
              Integrations
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage your connections with third-party services.
            </Typography>
          </Box>
          <Box sx={{ width: 120, height: 36 }}>
            <CircularProgress size={24} sx={{ visibility: 'hidden' }} />
          </Box>
        </Paper>

        <Paper sx={{ width: '100%' }}>
          <Box sx={{ p: 3 }}>
            <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: 'background.paper' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">{IntegrationCategory.DocumentRepository}</Typography>
                <Chip
                  label="Loading..."
                  color="default"
                  size="small"
                />
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                gap: 3
              }}>
                {Array.from({ length: 8 }).map((_, index) => (
                  <IntegrationCardSkeleton key={index} />
                ))}
              </Box>
            </Paper>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ flexGrow: 1 }}>
      {/* Enhanced Header */}
      <Paper sx={{ mb: 3, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4">
            Integrations
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your connections with third-party services.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={isSyncing ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
          onClick={fetchAndUpdateIntegrations}
          disabled={isSyncing}
        >
          {isSyncing ? 'Syncing...' : 'Sync All'}
        </Button>
      </Paper>

      <Paper sx={{ width: '100%' }}>
        <Box sx={{ p: 3 }}>
        {/* Integration Categories */}
        {/* Currently only showing Document Repository integrations as requested */}
        {(() => {
          const category = IntegrationCategory.DocumentRepository;
          const categoryIntegrations = integrations.filter(i => i.category === category);
          const connectedCount = categoryIntegrations.filter(i => i.isConnected).length;
          
          return (
            <Paper key={category} variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: 'background.paper' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">{category}</Typography>
                <Chip
                  label={`${connectedCount}/${categoryIntegrations.length} Connected`}
                  color={connectedCount > 0 ? 'success' : 'default'}
                  size="small"
                />
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                gap: 3
              }}>
                {categoryIntegrations.map((integration) => (
                  <Paper
                    key={integration.id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      borderRadius: 2,
                      transition: 'box-shadow 0.3s ease-in-out, border-color 0.3s ease-in-out',
                      '&:hover': {
                        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
                        borderColor: 'primary.main',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        src={integration.logoUrl}
                        sx={{ width: 48, height: 48 }}
                        variant="rounded"
                      >
                        {integration.name.charAt(0)}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" fontWeight="600">
                          {integration.name}
                        </Typography>
                        <Chip
                          label={integration.status}
                          color={
                            integration.status === IntegrationStatus.Connected
                                ? 'success'
                                : integration.status === IntegrationStatus.Error
                                ? 'error'
                                : integration.status === IntegrationStatus.Syncing
                                ? 'warning'
                                : integration.status === IntegrationStatus.ComingSoon
                                ? 'info'
                                : 'default'
                          }
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ minHeight: '40px', mt: 1 }}>
                      {integration.description}
                    </Typography>

                    {integration.isConnected && integration.lastSync && (
                      <Typography variant="caption" color="text.secondary">
                        Last sync: {new Date(integration.lastSync).toLocaleString()}
                      </Typography>
                    )}

                    <Divider sx={{ my: 1 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                      {integration.status === IntegrationStatus.ComingSoon ? (
                        <Button size="small" variant="outlined" disabled>
                          Coming Soon
                        </Button>
                      ) : integration.isConnected ? (
                        <>
                          <Button size="small" color="error" startIcon={<LinkOffIcon />} onClick={() => handleDisconnect(integration.user_integration_id!)} disabled={isLoading[integration.user_integration_id!]}>
                            {isLoading[integration.user_integration_id!] ? 'Disconnecting...' : 'Disconnect'}
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="contained"
                          startIcon={<LinkIcon />}
                          onClick={() => handleConnect(integration.id)}
                          disabled={isLoading[integration.id]}
                        >
                          {isLoading[integration.id] ? 'Connecting...' : 'Connect'}
                        </Button>
                      )}
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Paper>
          );
        })()}
        
        {/* Other integration categories are commented out as requested */}
        {/* Uncomment this section to show all integration categories */}
        </Box>
      </Paper>
    </Box>
    
    {/* Snackbar for notifications */}
    <Snackbar
      open={snackbar.open}
      autoHideDuration={6000}
      onClose={handleCloseSnackbar}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
        {snackbar.message}
      </Alert>
    </Snackbar>
    </>
  );
};

export default Integrations;
